import clsx from 'clsx';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { DropZoneArea } from '@/app/components/DropZoneArea';
import { Modal } from '@/app/components/Modal';
import { ModalMobileHeader } from '@/app/components/Modal/ModalMobileHeader';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { PreviewFileCard } from '@/app/components/cards/PreviewFileCard';
import { ALLOWED_EXTENSIONS, ALLOWED_EXTENSIONS_STRING, MAX_FILE_SIZE, MAX_FILE_SIZE_MB } from '@/consts/const';
import { useUploadFile } from '@/context/UploadFileProvider';
import { UploadFileWorkspaces } from '@/context/UploadFileProvider/types';
import { useFileDrop } from '@/hooks/useFileDrop';
import { isFileSizeValid } from '@/hooks/useFileDrop/utils/validations';
import { useUploadFileData } from '@/hooks/useUploadFileData';
import { useAddFile } from '@/hooks/useUploadFileData/hooks/useAddFile';
import { useFinalizeUpload } from '@/hooks/useUploadFileData/hooks/useFinalizeUpload';
import { useUpdateFile } from '@/hooks/useUploadFileData/hooks/useUpdateFile';
import type { ClientDocumentResponse } from '@/types/clientsApiTypes';
import type { DocumentType } from '@/types/documentsApiTypes';
import { formatMMDDYYYYToYYYYMMDD } from '@/utils/formatDate';
import { PassportForm } from './components/PassportForm';
import type { PassportFormData } from './components/PassportForm/types';
import FileIcon from '/public/icons/file.svg';
import UploadIcon from '/public/icons/upload-cloud.svg';

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data: ClientDocumentResponse, passportType: DocumentType) => void;
  passengerId?: number | string | null;
  documentsCount: number;
  isPublicPage?: boolean;
  filesLimit?: number;
}

const MAX_FILES_COUNT = 5;
const PASSPORT_MAX_FILE_SIZE = 1;

export const UploadDocumentModal = ({
  isOpen,
  onClose,
  onSuccess,
  passengerId,
  isPublicPage,
  filesLimit = MAX_FILES_COUNT,
}: UploadDocumentModalProps) => {
  const methods = useForm<PassportFormData>({
    defaultValues: {
      document_type: 'other',
      document_name: '',
      passport_number: '',
      issuance_country: '',
      issue_date: '',
      expiry_date: '',
    },
  });
  const [isPending, setIsPending] = useState(false);
  const { uploads, onSelectFiles, onDeleteAllFiles, isPendingFilesConversion } = useUploadFile();
  const { mutateAsync: addFile } = useAddFile();
  const { uploadFileData } = useUploadFileData();
  const { mutateAsync: finalizeUpload } = useFinalizeUpload();
  const { mutateAsync: updateFile } = useUpdateFile();

  const documentType = methods.watch('document_type');
  const files = uploads.filter((upload) => upload.status === 'pending').map((upload) => upload.file);
  const fileCountLimit = useMemo(() => {
    return documentType === 'passport' ? PASSPORT_MAX_FILE_SIZE : filesLimit;
  }, [documentType]);

  const onSelectFilesAndOpenUploadManager = useCallback(
    (newFiles: File[]) => {
      if (files.length < fileCountLimit) {
        onSelectFiles(newFiles, String(passengerId));
      }
    },
    [onSelectFiles, passengerId, files.length, fileCountLimit]
  );

  const { isDragging, inputRefProps, dropZoneProps, isAllowedExtensionError } = useFileDrop({
    setFiles: onSelectFilesAndOpenUploadManager,
    files,
    allowedExtensions: ALLOWED_EXTENSIONS,
    isMultipleFiles: true,
    maxFileSize: MAX_FILE_SIZE,
    maxFilesCount: fileCountLimit - files.length,
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const hasUnsupportedFiles = useMemo(
    () => files.some((file) => !isFileSizeValid(file, MAX_FILE_SIZE)),
    [files, fileCountLimit]
  );

  const isDisabledAddButton = useMemo(() => {
    return hasUnsupportedFiles || isPending || !files.length;
  }, [hasUnsupportedFiles, isPending, files.length]);

  const onAddDocumentWithoutFiles = async () => {
    setIsPending(true);
    try {
      const { document_type, document_name, passport_number, issuance_country, issue_date, expiry_date } =
        methods.getValues();
      const response = await addFile(
        {
          client_id: Number(passengerId),
          document_type,
          document_name: document_name || files[0].name || '',
          passport_number,
          issuance_country,
          issue_date: issue_date ? formatMMDDYYYYToYYYYMMDD(issue_date) : null,
          expiry_date: expiry_date ? formatMMDDYYYYToYYYYMMDD(expiry_date) : null,
          document: null,
        },
        {
          onSuccess: () => {
            const documentType = methods.getValues().document_type;
            onSuccess?.(response.data, documentType);
            onClose();
          },
        }
      );
    } catch {
      toast.error('Failed to add document');
    } finally {
      setIsPending(false);
    }
  };

  const onAddDocument = async () => {
    if (!files.length) {
      onAddDocumentWithoutFiles();
      return;
    }

    setIsPending(true);
    try {
      for (const file of files) {
        const { storage_id } = await uploadFileData(file);
        const { data } = await finalizeUpload({
          client_id: Number(passengerId),
          document: [{ storage_id, workspace: UploadFileWorkspaces.PASSPORT }],
        });
        const { document_type, document_name, passport_number, issuance_country, issue_date, expiry_date } =
          methods.getValues();
        const updatedFile = await updateFile({
          id: data.id,
          options: {
            document_type,
            document_name: document_name || file.name,
            passport_number,
            issuance_country,
            issue_date: issue_date ? formatMMDDYYYYToYYYYMMDD(issue_date) : null,
            expiry_date: expiry_date ? formatMMDDYYYYToYYYYMMDD(expiry_date) : null,
          },
        });
        const documentType = methods.getValues().document_type;
        onSuccess?.(updatedFile.data, documentType);
      }
    } catch {
      toast.error('Failed to add document');
    } finally {
      setIsPending(false);
      onClose();
      onDeleteAllFiles();
    }
  };

  const onUpload = () => {
    fileInputRef.current?.click();
  };

  const onCloseModal = () => {
    onClose();
    onDeleteAllFiles();
  };

  return (
    <Modal open={isOpen} onClose={onCloseModal} variant="fullScreenMobile">
      <div className="max-w-[574px] min-w-[574px] w-full bg-white rounded-[16px] py-[46px] md:max-h-[90dvh] md:overflow-y-auto max-md:flex max-md:h-full max-md:min-w-0 max-md:max-w-none max-md:flex-col max-md:rounded-none max-md:py-4">
        <div className="flex flex-col gap-[6px] items-center justify-center px-9 max-md:items-stretch max-md:px-4">
          <p className="text-[1.75rem] font-light leading-[1.3] font-canela text-blue-900 text-center max-md:hidden">
            Add new document
          </p>
          <div className="hidden max-md:block">
            <ModalMobileHeader title="Add new document" onBack={onCloseModal} />
          </div>
          <p className="text-body-regular text-grey-700 text-center px-6 max-md:px-0 max-md:text-left max-md:text-body-small">
            This information will be securely saved to the traveler&apos;s profile and available for future bookings.
          </p>
        </div>
        <div className="max-h-[55vh] h-full overflow-y-auto px-9 max-md:max-h-none max-md:min-h-0 max-md:flex-1 max-md:px-4">
          <FormProvider {...methods}>
            <PassportForm />
          </FormProvider>
          {!isPublicPage && (
            <section className="w-full rounded-[14px] bg-blue-50 my-4 px-6 py-3">
              <header>
                <p className="text-button-semibold text-black">Passport upload guidelines</p>
              </header>
              <ul className="list-disc ml-6 text-body-small !leading-[150%] text-grey-700 mt-[2px]">
                <li>
                  Upload a clear image of the <strong className="font-medium text-black">photo page</strong> (the page
                  with your picture).
                </li>
                <li>
                  Make sure <strong className="font-medium text-black">all 4 corners are visible</strong> and text is
                  readable.
                </li>
                <li>
                  Avoid <strong className="font-medium text-black">glare, shadows, blur,</strong> or fingers covering
                  any info.
                </li>
              </ul>
            </section>
          )}
          {uploads.length > 0 ? (
            <div className="flex flex-col">
              <BaseButton
                startIcon={<UploadIcon className="w-[18px] h-[18px]" />}
                intent="secondary"
                size="small"
                onClick={onUpload}
                disabled={files.length >= fileCountLimit || isPendingFilesConversion}
                type="button"
              >
                {isPendingFilesConversion ? 'Preparing files...' : 'Upload More Files'}
              </BaseButton>
              <div
                className={clsx(
                  'grid grid-cols-6 gap-4 overflow-y-auto px-2.5 max-h-[160px] auto-rows-[72px] max-md:grid-cols-3 max-md:px-0',
                  files.length && 'mt-5'
                )}
              >
                {files.map((file) => (
                  <PreviewFileCard key={file.name} file={file} />
                ))}
              </div>
            </div>
          ) : (
            <DropZoneArea
              {...dropZoneProps}
              uploadIcon={FileIcon}
              className={clsx('mt-5 px-[50px] py-[36px] max-md:px-4 max-md:py-6', {
                'bg-red-50': isAllowedExtensionError || hasUnsupportedFiles,
              })}
              isDragging={isDragging}
              isError={isAllowedExtensionError || hasUnsupportedFiles}
            >
              <div className="flex flex-col gap-[10px] items-center justify-center">
                <p className="text-button-medium text-black mt-5">Drag your file(s) to start uploading</p>
                <div className="flex flex-row gap-[12px] items-center justify-center">
                  <p className="w-[82px] h-[1px] bg-grey-400" />
                  <p className="text-body-small text-neutral-500">or</p>
                  <p className="w-[82px] h-[1px] bg-grey-400" />
                </div>
                <BaseButton
                  startIcon={<UploadIcon className="w-4 h-4 text-blue-300 fill-current" />}
                  intent="ghost"
                  disabled={isPendingFilesConversion}
                  size="medium"
                  onClick={onUpload}
                >
                  {isPendingFilesConversion ? 'Preparing files...' : 'Upload Files'}
                </BaseButton>
              </div>
              <p className="text-body-small text-neutral-500 mt-[18px] text-center">
                PDF, HEIC, JPG or PNG (max {MAX_FILE_SIZE_MB}MB per file)
              </p>
              {hasUnsupportedFiles && (
                <p className="text-center text-red-500 text-tp-body-small mt-4">File size limits</p>
              )}
              {isAllowedExtensionError && (
                <p className="text-center text-red-500 text-tp-body-small mt-4">File type(s) not supported</p>
              )}
            </DropZoneArea>
          )}
        </div>
        <div className="flex flex-row gap-5 items-center justify-center w-full mt-9 px-9 max-md:mt-auto max-md:shrink-0 max-md:gap-3 max-md:px-4 max-md:pt-4 max-md:pb-2">
          <BaseButton intent="secondary" size="full" onClick={onCloseModal}>
            Cancel
          </BaseButton>
          <BaseButton disabled={isDisabledAddButton} intent="primary" size="full" onClick={onAddDocument}>
            {isPending ? 'Adding...' : 'Add Document'}
          </BaseButton>
        </div>
      </div>
      <input
        accept={ALLOWED_EXTENSIONS_STRING}
        ref={fileInputRef}
        type="file"
        id="fileInput"
        style={{ display: 'none' }}
        {...inputRefProps}
      />
    </Modal>
  );
};
